import { useCallback, useEffect, useState } from 'react';
import type { User, BusinessUser, DrawerTabType } from '../../../types/usersDemo';
import { mockDrawerRequests, mockDrawerSupportTickets } from '../../../data/mock/usersDemo';

export interface PendingDrawerChange {
  id: number;
  newStatus: string;
  type: 'request' | 'support';
}

export interface UseUsersDemoDrawer {
  isOpen: boolean;
  open: (contact: User | BusinessUser, initialTab?: DrawerTabType) => void;
  close: () => void;
  setOpenerRef?: (el: HTMLElement | null) => void;

  activeTab: DrawerTabType;
  setActiveTab: (tab: DrawerTabType) => void;

  selectedContact: User | BusinessUser | null;

  showIdModal: boolean;
  setShowIdModal: (open: boolean) => void;

  editingDrawerRequestId: number | null;
  setEditingDrawerRequestId: (id: number | null) => void;
  editingDrawerSupportId: number | null;
  setEditingDrawerSupportId: (id: number | null) => void;

  pendingChange: PendingDrawerChange | null;
  setPendingChange: (c: PendingDrawerChange | null) => void;
  confirmPendingChange: () => Promise<void>;

  refreshTrigger: number;
}

export const useUsersDemoDrawer = (): UseUsersDemoDrawer => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DrawerTabType>('profile');
  const [selectedContact, setSelectedContact] = useState<User | BusinessUser | null>(null);
  const [showIdModal, setShowIdModal] = useState(false);
  const [editingDrawerRequestId, setEditingDrawerRequestId] = useState<number | null>(null);
  const [editingDrawerSupportId, setEditingDrawerSupportId] = useState<number | null>(null);
  const [pendingChange, setPendingChange] = useState<PendingDrawerChange | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [openerRef, setOpenerRef] = useState<HTMLElement | null>(null);

  const open = useCallback((contact: User | BusinessUser, initialTab: DrawerTabType = 'profile') => {
    setSelectedContact(contact);
    setActiveTab(initialTab);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSelectedContact(null);
    // restore focus to the control that opened the drawer
    setTimeout(() => openerRef?.focus?.(), 0);
  }, []);

  // Close inline dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingDrawerRequestId !== null || editingDrawerSupportId !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-status-dropdown]')) {
          setEditingDrawerRequestId(null);
          setEditingDrawerSupportId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingDrawerRequestId, editingDrawerSupportId]);

  const confirmPendingChange = useCallback(async () => {
    if (!pendingChange) return;

    try {
      if (pendingChange.type === 'request') {
        const idx = mockDrawerRequests.findIndex(r => r.id === pendingChange.id);
        if (idx !== -1) {
          mockDrawerRequests[idx].status = pendingChange.newStatus;
        }
      } else if (pendingChange.type === 'support') {
        const idx = mockDrawerSupportTickets.findIndex(t => t.id === pendingChange.id);
        if (idx !== -1) {
          mockDrawerSupportTickets[idx].status = pendingChange.newStatus;
        }
      }

      setRefreshTrigger(prev => prev + 1);
      setPendingChange(null);
      setEditingDrawerRequestId(null);
      setEditingDrawerSupportId(null);
    } catch (e) {
      // no-op; errors are unlikely with local mocks
    }
  }, [pendingChange]);

  return {
    isOpen,
    open,
    close,
    setOpenerRef,
    activeTab,
    setActiveTab,
    selectedContact,
    showIdModal,
    setShowIdModal,
    editingDrawerRequestId,
    setEditingDrawerRequestId,
    editingDrawerSupportId,
    setEditingDrawerSupportId,
    pendingChange,
    setPendingChange,
    confirmPendingChange,
    refreshTrigger,
  };
};

export default useUsersDemoDrawer;


